import { enhancedApi as billingApi } from '@store/generated/billing';
import { enhancedApi as claimsApi } from '@store/generated/claims';

/**
 * Feature-specific tag types for readiness cache management
 */
const READINESS_TAG_TYPES = ['OrgReadiness', 'PayerReadiness', 'ClaimReadiness'] as const;

/**
 * Billing Readiness API - extends the generated OpenAPI clients
 * Readiness endpoints are split across billing and claims APIs
 */
export const billingReadinessApi = billingApi.enhanceEndpoints({
  addTagTypes: READINESS_TAG_TYPES,
  endpoints: {
    // Provider org readiness
    getV2ProviderBillingReadinessOrg: {
      providesTags: [{ type: 'OrgReadiness', id: 'PROVIDER' }],
    },

    // Provider payer readiness
    getV2ProviderBillingReadinessBillingEntitiesByBillingEntityIdPayersAndPayerId: {
      providesTags: (_result, _error, arg) => [
        { type: 'PayerReadiness', id: `${arg.billingEntityId}-${arg.payerId}` },
      ],
    },

    // Admin org readiness
    getV2AdminOrgsByOrganizationIdBillingReadiness: {
      providesTags: (_result, _error, arg) => [
        { type: 'OrgReadiness', id: arg.organizationId },
      ],
    },

    // Admin payer readiness
    getV2AdminOrgsByOrganizationIdBillingEntitiesAndBillingEntityIdPayersPayerIdReadiness: {
      providesTags: (_result, _error, arg) => [
        {
          type: 'PayerReadiness',
          id: `${arg.organizationId}-${arg.billingEntityId}-${arg.payerId}`,
        },
      ],
    },

    // Admin claim readiness
    getV2AdminClaimsByClaimIdReadiness: {
      providesTags: (_result, _error, arg) => [{ type: 'ClaimReadiness', id: arg.claimId }],
    },
  },
});

/**
 * Claims Readiness API - extends the claims API for claim-level readiness
 */
export const claimsReadinessApi = claimsApi.enhanceEndpoints({
  addTagTypes: READINESS_TAG_TYPES,
  endpoints: {
    // Provider claim readiness
    getV2ProviderClaimsByClaimIdReadiness: {
      providesTags: (_result, _error, arg) => [{ type: 'ClaimReadiness', id: arg.claimId }],
    },
  },
});

// Export hooks with user-friendly names only (Provider endpoints)
export const {
  useGetV2ProviderBillingReadinessOrgQuery: useGetOrgReadinessQuery,
  useLazyGetV2ProviderBillingReadinessOrgQuery: useLazyGetOrgReadinessQuery,
  useGetV2ProviderBillingReadinessBillingEntitiesByBillingEntityIdPayersAndPayerIdQuery:
    useGetPayerReadinessQuery,
  useLazyGetV2ProviderBillingReadinessBillingEntitiesByBillingEntityIdPayersAndPayerIdQuery:
    useLazyGetPayerReadinessQuery,

  // Admin endpoints
  useGetV2AdminOrgsByOrganizationIdBillingReadinessQuery: useGetOrgReadinessAdminQuery,
  useLazyGetV2AdminOrgsByOrganizationIdBillingReadinessQuery: useLazyGetOrgReadinessAdminQuery,
  useGetV2AdminOrgsByOrganizationIdBillingEntitiesAndBillingEntityIdPayersPayerIdReadinessQuery:
    useGetPayerReadinessAdminQuery,
  useLazyGetV2AdminOrgsByOrganizationIdBillingEntitiesAndBillingEntityIdPayersPayerIdReadinessQuery:
    useLazyGetPayerReadinessAdminQuery,
  useGetV2AdminClaimsByClaimIdReadinessQuery: useGetClaimReadinessAdminQuery,
  useLazyGetV2AdminClaimsByClaimIdReadinessQuery: useLazyGetClaimReadinessAdminQuery,
} = billingReadinessApi;

// Export claim readiness hooks
export const {
  useGetV2ProviderClaimsByClaimIdReadinessQuery: useGetClaimReadinessQuery,
  useLazyGetV2ProviderClaimsByClaimIdReadinessQuery: useLazyGetClaimReadinessQuery,
} = claimsReadinessApi;
